"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ModalSheetSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const maxWidthClass: Record<ModalSheetSize, string> = {
  sm: "md:max-w-[452px]",
  md: "md:max-w-xl",
  lg: "md:max-w-2xl",
  xl: "md:max-w-3xl",
  "2xl": "md:max-w-4xl",
  "3xl": "md:max-w-[1120px]",
};

/** Duration for the close (exit) animation -- fast and snappy. */
const CLOSE_DURATION_MS = 250;
const SWIPE_CLOSE_THRESHOLD_PX = 120;

type Props = {
  isOpen: boolean;
  size?: ModalSheetSize;
  /**
   * Called when the user dismisses the sheet (backdrop click, swipe down, Escape).
   * The parent is responsible for setting isOpen=false in response.
   */
  onClose?: () => void;
  /**
   * When true, raises the backdrop + sheet above the default modal layer so a
   * nested sheet (e.g. an actions menu opened on top of another modal) dims and
   * sits above the modal beneath it. Use only for stacked-on-top sheets.
   */
  elevated?: boolean;
  /**
   * Panel background. "content" (default) uses the app gray so a body card can
   * float on it (the form-modal look); "card" uses the plain surface for
   * compact white dialogs (confirm/alert).
   */
  surface?: "content" | "card";
  /**
   * Presentation:
   *  - "sheet" (default): bottom sheet on mobile (drag-to-dismiss), centered
   *    modal on desktop.
   *  - "dialog": always centered with a scale/fade entrance on every
   *    breakpoint — no bottom-sheet slide, drag handle, or swipe. Use for
   *    compact confirm/alert dialogs.
   *  - "drawer": bottom sheet on mobile (same as "sheet"), but on desktop a
   *    full-height panel anchored to the end edge that slides in from the side.
   */
  variant?: "sheet" | "dialog" | "drawer";
  children: React.ReactNode;
};

/**
 * Responsive modal wrapper.
 *
 * Mobile  -> slides up from bottom as a sheet; draggable down to dismiss.
 * Desktop -> centered modal with subtle scale + fade entrance.
 *
 * iOS safe area is handled automatically via a spacer at the bottom.
 * No child needs to add env(safe-area-inset-bottom) manually.
 */
export function ModalSheet({ isOpen, size = "md", onClose, elevated = false, surface = "content", variant = "sheet", children }: Props) {
  const isDialog = variant === "dialog";
  const isDrawer = variant === "drawer";
  // Desktop drawer slide direction. `md:justify-end` anchors the panel at the
  // inline-end (right in LTR, left in RTL); the off-screen transform must match,
  // so compute it from `dir` rather than hardcoding translate-x-full (which slid
  // the wrong way in Arabic).
  const drawerClosedX =
    typeof document !== "undefined" && document.documentElement.dir === "rtl"
      ? "md:-translate-x-full"
      : "md:translate-x-full";

  // Freeze the rendered children through the close animation. Parents commonly
  // clear the backing state (e.g. `setSelected(null)`) in the same tick they
  // close the modal, which would otherwise flash the empty/default content for
  // the ~250ms exit. While open we track the latest children; while closing we
  // keep showing the last ones.
  const frozenChildrenRef = useRef<React.ReactNode>(children);
  useEffect(() => {
    if (isOpen) frozenChildrenRef.current = children;
  }, [isOpen, children]);
  // isMounted stays true during the close animation so the element is in DOM.
  const [isMounted, setIsMounted] = useState(isOpen);
  // isVisible drives the CSS animation class (translate-y-0 vs translate-y-full).
  const [isVisible, setIsVisible] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  // Stable ref to onClose so event listeners never go stale.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Guard: if a swipe-dismiss animation is already running, ignore the isOpen=false
  // that the parent fires in response to our onClose call.
  const isSwipeClosingRef = useRef(false);

  // Animate close triggered by the parent setting isOpen=false.
  const animateClose = useCallback(() => {
    if (isSwipeClosingRef.current) return; // swipe already handling it
    setIsVisible(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsMounted(false);
    }, CLOSE_DURATION_MS);
  }, []);

  // React to isOpen prop changes.
  useEffect(() => {
    if (isOpen) {
      isSwipeClosingRef.current = false;
      setIsMounted(true);
      // Double-RAF ensures a real paint frame between mount and the class
      // change so the CSS transition always plays reliably.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      animateClose();
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen, animateClose]);

  // Focus trap -- lock focus within the modal while open.
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    previouslyFocusedRef.current = document.activeElement;

    // Wait for mount before querying focusable elements.
    const raf = requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = sheet.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length > 0) focusable[0].focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      // Restore focus to the element that was focused before the modal opened.
      const prev = previouslyFocusedRef.current;
      if (prev && prev instanceof HTMLElement) {
        prev.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = sheet.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [isOpen]);

  // Body scroll lock -- prevent background scrolling while the sheet is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Escape key.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // --- Drag to dismiss -------------------------------------------------------
  // All drag state lives in refs -- zero React re-renders during touchmove.
  const touchStartY = useRef<number>(0);
  const currentDragY = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.changedTouches[0]?.clientY ?? 0;
    currentDragY.current = 0;
    // Disable CSS transition while the user is dragging (instant follow).
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Only allow dragging downward.
    const delta = Math.max(0, (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current);
    currentDragY.current = delta;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${delta}px)`;
    }
    // Proportionally dim the backdrop as the sheet is dragged down.
    if (backdropRef.current) {
      const opacity = Math.max(0, 0.4 - (delta / 300) * 0.4);
      backdropRef.current.style.opacity = String(opacity);
    }
  };

  const handleTouchEnd = () => {
    const delta = currentDragY.current;

    // Re-enable the CSS transition.
    if (sheetRef.current) sheetRef.current.style.transition = "";

    if (delta > SWIPE_CLOSE_THRESHOLD_PX) {
      // Past threshold -- continue sliding off screen with CSS transition.
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(100%)";
      }
      // Fade out the backdrop in sync.
      if (backdropRef.current) {
        backdropRef.current.style.transition = `opacity ${CLOSE_DURATION_MS}ms ease-in`;
        backdropRef.current.style.opacity = "0";
      }
      setTimeout(() => {
        // Reset all inline overrides.
        if (sheetRef.current) sheetRef.current.style.transform = "";
        if (backdropRef.current) {
          backdropRef.current.style.transition = "";
          backdropRef.current.style.opacity = "";
        }
        // Unmount and notify parent -- set the guard so the parent's isOpen=false
        // response doesn't trigger a duplicate close animation.
        isSwipeClosingRef.current = true;
        setIsMounted(false);
        onCloseRef.current?.();
      }, CLOSE_DURATION_MS);
    } else {
      // Below threshold -- snap back to open position.
      if (sheetRef.current) sheetRef.current.style.transform = "";
      if (backdropRef.current) backdropRef.current.style.opacity = "";
      currentDragY.current = 0;
    }
  };

  const handleTouchCancel = () => {
    // System-interrupted gesture -- snap back cleanly.
    if (sheetRef.current) {
      sheetRef.current.style.transition = "";
      sheetRef.current.style.transform = "";
    }
    if (backdropRef.current) backdropRef.current.style.opacity = "";
    currentDragY.current = 0;
  };
  // ---------------------------------------------------------------------------

  if (!isMounted) return null;
  if (typeof document === "undefined") return null;

  // Portal to <body> so the sheet's `position: fixed` anchors to the viewport
  // even when an ancestor is transformed (e.g. a page-transition container),
  // which would otherwise become the containing block and mis-place the modal.
  return createPortal(
    <>
      {/* Backdrop. Elevated sheets stack on a base backdrop, so they tint only
          lightly to avoid compounding into near-black; the full-bleed (order)
          sheet sits over a dark page so it also tints softer than the default. */}
      <div
        ref={backdropRef}
        className={[
          "fixed inset-0 transition-opacity",
          // Elevated (stacked) sheets sit on a base backdrop, so they tint only
          // lightly to avoid compounding into near-black.
          elevated ? "z-[60] bg-black/10" : "z-40 bg-black/40",
          // Backdrop mirrors the sheet: slow fade-in, fast fade-out
          isVisible ? "opacity-100 duration-[450ms]" : "opacity-0 duration-[250ms]",
        ].join(" ")}
        aria-hidden="true"
        onClick={() => onCloseRef.current?.()}
      />

      {/* Sheet positioner -- pointer-events-none so clicks pass through to backdrop */}
      <div className={[
        "pointer-events-none fixed inset-0 flex",
        // Dialog: centered with padding on every breakpoint.
        // Drawer: bottom sheet on mobile, end-anchored full-height on desktop.
        // Sheet: bottom-anchored on mobile, centered on desktop.
        isDialog
          ? "items-center justify-center p-6"
          : isDrawer
            ? "items-end md:items-stretch md:justify-end"
            : "items-end md:items-center md:justify-center md:p-6",
        elevated ? "z-[70]" : "z-50",
      ].join(" ")}>
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          onTouchStart={isDialog ? undefined : handleTouchStart}
          onTouchMove={isDialog ? undefined : handleTouchMove}
          onTouchEnd={isDialog ? undefined : handleTouchEnd}
          onTouchCancel={isDialog ? undefined : handleTouchCancel}
          className={[
            "pointer-events-auto flex w-full flex-col",
            // Panel sits on the app "content" gray so the body card floats as a
            // white inset (new modal look). Falls back to the surface colour in
            // apps that don't define --content. "card" surface keeps the plain
            // white panel for compact confirm/alert dialogs.
            "border border-[color:var(--border-light)] shadow-[var(--shadow-lg)]",
            surface === "card"
              ? "bg-[color:var(--surface)]"
              : "bg-[color:var(--content,var(--surface))]",
            // Transition applies to transform + opacity + scale.
            // The duration/easing in each state applies while LEAVING that state,
            // so open-curve lives in the closed class and close-curve in the open class.
            "transition-all will-change-transform",
            isDialog
              ? // Centered scale/fade on all breakpoints (classic alert dialog).
                `max-h-[90vh] rounded-[18px] ${maxWidthClass[size]} ${
                  isVisible
                    ? "opacity-100 scale-100 duration-[250ms] ease-in"
                    : "opacity-0 scale-[0.97] duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                }`
              : isDrawer
                ? // Bottom sheet (mobile) -> end-anchored full-height panel (desktop).
                  [
                    "max-h-[92vh] rounded-t-[20px]",
                    `md:h-[100dvh] md:max-h-[100dvh] md:rounded-none md:rounded-s-[18px] md:border-s ${maxWidthClass[size]}`,
                    isVisible
                      ? "translate-y-0 opacity-100 md:translate-x-0 md:opacity-100 duration-[250ms] ease-in"
                      : `translate-y-full opacity-100 md:translate-y-0 ${drawerClosedX} md:opacity-100 duration-[250ms] ease-[cubic-bezier(0.4,0,1,1)]`,
                  ].join(" ")
                : // Bottom sheet (mobile) -> centered modal (desktop).
                  [
                    "max-h-[92vh] rounded-t-[20px]",
                    `md:rounded-[18px] md:max-h-[90vh] ${maxWidthClass[size]}`,
                    isVisible
                      ? "translate-y-0 opacity-100 md:scale-100 duration-[250ms] ease-in"
                      : "translate-y-full md:translate-y-0 md:scale-[0.97] md:opacity-0 duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
                  ].join(" "),
          ].join(" ")}
        >
          {/* Drag handle pill -- bottom-sheet affordance, mobile only */}
          {isDialog ? null : (
            <div className="flex shrink-0 justify-center pb-1 pt-2.5 md:hidden" aria-hidden="true">
              <div className="h-1 w-10 rounded-full bg-[color:var(--border-light)]" />
            </div>
          )}

          {isOpen ? children : frozenChildrenRef.current}

          {/*
           * Safe area spacer -- equals the iOS home-indicator inset for the
           * bottom sheet. Centered dialogs don't touch the home indicator.
           */}
          {isDialog ? null : (
            <div aria-hidden="true" className="h-[env(safe-area-inset-bottom)] shrink-0" />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
